import { computed, inject, Injectable, signal } from '@angular/core';
import { IVehicleDetailsBFF } from '@core/interface';
import { ADVISOR_TYPE } from '../../utils/advisors.utils';
import { VEHICLE_MODEL } from '../../utils/vehicles-names.utils';
import { DealerSpecificServices, SchedulingModalityEnum, StorageKeys } from '@core/enums';
import { VehicleHelperService } from './vehicle-helper.service';
import { ScheduleCoreService } from '../schedule/schedule.service';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { API, environment } from '@core/envs';

@Injectable({
  providedIn: 'root',
})
export class VehicleService extends VehicleHelperService {
  readonly #http = inject(HttpClient);

  readonly scheduleCoreService = inject(ScheduleCoreService);

  readonly #vehicleDetailsSignal = signal<IVehicleDetailsBFF | null>(null);
  readonly vehicleComputed = computed<IVehicleDetailsBFF | null>(() => this.#vehicleDetailsSignal());

  /**
   * Define os detalhes veículo atual no signal.
   *
   * @param vehicleDetails
   */
  setVehicleDetails(vehicleDetails: IVehicleDetailsBFF): void {
    this.#vehicleDetailsSignal.set(vehicleDetails);
    sessionStorage.setItem(StorageKeys.VEHICLE_DETAILS, JSON.stringify(vehicleDetails));
  }

  /**
   * Recupera os detalhes do veículo atual do signal ou do sessionStorage.
   *
   * @returns IVehicleDetailsBFF | null
   */
  getVehicleDetails(): IVehicleDetailsBFF | null {
    if (this.#vehicleDetailsSignal()) {
      return this.#vehicleDetailsSignal();
    }
    const storedVehicleDetails = sessionStorage.getItem(StorageKeys.VEHICLE_DETAILS);
    if (storedVehicleDetails) {
      const parsedVehicleDetails: IVehicleDetailsBFF = JSON.parse(storedVehicleDetails);
      this.#vehicleDetailsSignal.set(parsedVehicleDetails);
      return parsedVehicleDetails;
    }
    return null;
  }

  // Save VIN to storage
  saveVin(vin: string): void {
    sessionStorage.setItem(StorageKeys.VIN, vin);
  }

  // Retrieve VIN from storage
  getVin(): string {
    return sessionStorage.getItem(StorageKeys.VIN) ?? '';
  }

  // Remove VIN from storage
  clearVin(): void {
    sessionStorage.removeItem(StorageKeys.VIN);
  }

  // Check if VIN exists
  hasVin(): boolean {
    return !!this.getVin();
  }

  getAdvisorType(modelName: string): string {
    return this.isMobileServiceVan() ? ADVISOR_TYPE.MSV : this.isTransit(modelName) ? ADVISOR_TYPE.REGULAR_TRANSIT : ADVISOR_TYPE.REGULAR;
  }

  isTransit(modelName: string): boolean {
    return modelName?.toUpperCase().includes(VEHICLE_MODEL.TRANSIT);
  }

  isMustangVehicle(): boolean {
    const name = this.getVehicleDetails()?.modelName ?? '';
    const isMustang = this.isMustangVehicleHelper(name);
    const type = isMustang ? DealerSpecificServices.mustang.toLowerCase() : undefined;
    this.#vehicleDetailsSignal.update(vehicle => vehicle ? { ...vehicle, type: type } : vehicle);
    return isMustang;
  }

  isElectricalVehicle(): boolean {
    const fuelType = this.getVehicleDetails()?.fuelType ?? '';
    const isElectrical = this.isElectricalVehicleHelper(fuelType);
    const type = isElectrical ? DealerSpecificServices.electric.toLocaleLowerCase() : undefined;
    this.#vehicleDetailsSignal.update(vehicle => vehicle ? { ...vehicle, type: type } : vehicle);
    return isElectrical;
  }

  isMobileServiceVan(): boolean {
    const scheduleModality = this.scheduleCoreService.getModality(); 
    return scheduleModality === SchedulingModalityEnum.MSV;
  }

  getIsCommercialVehicle(vin: string, country: string): Observable<boolean> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/vehicles/v2/commercial`;
    return this.#http.post<number>(url, { vin, country }).pipe(map(value => value !== 0));
  }
}
